import React from 'react';

/**
 * Drops a schema.org JSON-LD block into the DOM head-or-body so AI search
 * crawlers (ChatGPT Search, Perplexity, Claude Search, Google AI Overviews)
 * have structured metadata to summarize the page. LLMs weight
 * `description` + `name` heavily when answering questions about a URL.
 */
const JsonLd = ({ data }) => {
  if (!data) return null;
  return (
    <script
      type="application/ld+json"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
};

/** Build a JSON-LD payload for a Today recommendation. */
export const todayJsonLd = ({ city, latitude, longitude, recommendation, displayTemp, forecastHours }) => {
  if (!recommendation || !city) return null;
  const whenLabel = forecastHours === 0 ? 'right now'
    : forecastHours === -24 ? 'yesterday'
    : `in ${forecastHours} hours`;
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: `How many layers in ${city} ${whenLabel}`,
    description: displayTemp != null
      ? `${city} ${whenLabel}: ${Math.round(displayTemp)}°C, ${recommendation.description}. layers.today recommends ${recommendation.layers} layer${recommendation.layers === 1 ? '' : 's'}.`
      : `${city}: ${recommendation.description}, ${recommendation.layers} layer${recommendation.layers === 1 ? '' : 's'} recommended.`,
    about: {
      '@type': 'Place',
      name: city,
      ...(latitude != null && longitude != null && {
        geo: { '@type': 'GeoCoordinates', latitude, longitude }
      })
    },
    primaryImageOfPage: 'https://layers.today/og-image.svg',
    publisher: { '@type': 'Person', name: 'Vijith Quadros' }
  };
};

/** Build a JSON-LD payload for a Trip plan. */
export const tripJsonLd = ({ data, returnDate, homeName }) => {
  if (!Array.isArray(data) || !data.length) return null;
  const cities = data.map((d) => d.stop?.name).filter(Boolean);
  const allDays = data.flatMap((d) => d.days || []);
  if (!allDays.length) return null;
  const lows = allDays.map((d) => d.temperature?.low).filter((v) => typeof v === 'number');
  const highs = allDays.map((d) => d.temperature?.high).filter((v) => typeof v === 'number');
  const layerCounts = allDays.map((d) => d.recommendation?.layers).filter((n) => n != null);
  const mode = layerCounts.length
    ? [...layerCounts].sort((a, b) =>
        layerCounts.filter((v) => v === a).length - layerCounts.filter((v) => v === b).length
      ).pop()
    : null;
  const route = cities.join(' → ');
  return {
    '@context': 'https://schema.org',
    '@type': 'TravelAction',
    name: `Trip packing plan: ${route}`,
    description: `Pack for ${Math.round(Math.min(...lows))}°–${Math.round(Math.max(...highs))}°C across ${cities.length} stop${cities.length === 1 ? '' : 's'}. layers.today recommends ${mode} layer${mode === 1 ? '' : 's'} most days.${homeName ? ` Returning home to ${homeName}.` : ''}`,
    fromLocation: cities[0] ? { '@type': 'Place', name: cities[0] } : undefined,
    toLocation: cities[cities.length - 1] ? { '@type': 'Place', name: cities[cities.length - 1] } : undefined,
    primaryImageOfPage: 'https://layers.today/og-image.svg'
  };
};

export default JsonLd;
